import _ from 'lodash';
import { lookupsModels } from 'utils/constants';
import { queryKeys } from './keys';

export const clearCache = {
  onCandidateChange: (queryClient, { candidateId, jobId }) => {
    queryClient.removeQueries({
      queryKey: queryKeys.candidate(candidateId),
    });
    queryClient.removeQueries([queryKeys.candidates()[0]]);
    queryClient.invalidateQueries([queryKeys.job(jobId)[0]]);
    queryClient.invalidateQueries([queryKeys.jobs()[0]]);
    queryClient.invalidateQueries([queryKeys.talentGroup()[0]]);
  },
  onTalentGroupChange: (queryClient, { groupId }) => {
    queryClient.invalidateQueries(queryKeys.talentGroups());
    queryClient.invalidateQueries(queryKeys.talentGroup(groupId));
  },

  onCategoryChange: (queryClient) => {
    queryClient.invalidateQueries([queryKeys.trainingsCategories()[0]]);
    queryClient.invalidateQueries(queryKeys.trainings());
  },
  onLookupChange: (queryClient, model) => {
    queryClient.invalidateQueries({
      // clear only if model used in lookups
      predicate: ({ queryKey }) =>
        queryKey?.length > 1 &&
        queryKey[0] === queryKeys.lookups({ models: [] })[0] &&
        queryKey[1].includes(model),
    });
  },
  onJobTitleChange(queryClient) {
    queryClient.invalidateQueries(queryKeys.jobTitles());
    this.onLookupChange(queryClient, lookupsModels.jobTitle);
  },
  onDepartmentChange(queryClient) {
    queryClient.invalidateQueries(queryKeys.departments());
    this.onLookupChange(queryClient, lookupsModels.department);
  },
  onDivisionChange(queryClient) {
    queryClient.invalidateQueries(queryKeys.divisions());
    this.onLookupChange(queryClient, lookupsModels.division);
  },
  onLocationChange(queryClient) {
    queryClient.invalidateQueries(queryKeys.locations());
    this.onLookupChange(queryClient, lookupsModels.location);
  },
  onJobsChange(queryClient) {
    queryClient.invalidateQueries([queryKeys.jobs()[0]]);
    queryClient.invalidateQueries([queryKeys.jobOpening()[0]]);
  },
  onJobChange(queryClient, { isDelete, jobId }) {
    queryClient.invalidateQueries([queryKeys.jobs()[0]]);
    queryClient.invalidateQueries([queryKeys.jobOpening()[0]]);
    if (isDelete) {
      queryClient.removeQueries(queryKeys.job(jobId));
    } else {
      queryClient.invalidateQueries([queryKeys.job(jobId)[0]]);
    }
  },
  onTimeOffPolicyChange(queryClient) {
    queryClient.invalidateQueries([queryKeys.userPolicy()[0]]);
    queryClient.invalidateQueries([queryKeys.policy()[0]]);
    queryClient.invalidateQueries([queryKeys.timeOffPolicies()[0]]);
  },
  onTimeOffBalanceChange(queryClient, { employeeId } = {}) {
    queryClient.invalidateQueries([queryKeys.timeOffBalance()[0]]);

    if (employeeId) {
      queryClient.invalidateQueries(
        queryKeys.timeOffPolicies(false, employeeId),
      );
    }

    queryClient.invalidateQueries([queryKeys.timeOffPolicies()[0]]);
  },
  onChangeEmployee(queryClient, userId, isMyProfile) {
    queryClient.invalidateQueries(queryKeys.employee(userId));
    queryClient.invalidateQueries([queryKeys.employeeProfile()[0]]);
    queryClient.invalidateQueries([queryKeys.employees(true)[0]]);
    queryClient.invalidateQueries([queryKeys.employeeSubordinates()[0]]);
    queryClient.invalidateQueries([queryKeys.notifications()[0]]);
    if (isMyProfile) {
      queryClient.invalidateQueries(queryKeys.user());
    }
    this.onChangeEmployeeStatus(queryClient);
  },

  onChangeEmployeeStatus(queryClient) {
    queryClient.invalidateQueries(queryKeys.employeeStatuses());
    this.onChangeEmployeeStatusRecord(queryClient);
  },

  onChangeEmployeeStatusRecord(queryClient, { employeeId = '' } = {}) {
    if (employeeId) {
      queryClient.invalidateQueries(
        queryKeys.employeeStatusRecords(employeeId),
      );
      queryClient.invalidateQueries(queryKeys.employee(employeeId));
      queryClient.invalidateQueries([queryKeys.reasons()[0]]);
    } else {
      queryClient.invalidateQueries([queryKeys.employeeStatusRecords()[0]]);
    }
  },
  onChangeReason(queryClient, type) {
    queryClient.invalidateQueries(
      queryKeys.reasons({ filter: { byType: type } }),
    );
    queryClient.invalidateQueries([queryKeys.employeeBonusRecords()[0]]);
  },
  onChangePaySchedule(queryClient) {
    queryClient.invalidateQueries(queryKeys.paySchedules());
    queryClient.invalidateQueries([queryKeys.employeeCompensationRecords()[0]]);
  },
  onChangeCompensationRecord(queryClient, employeeId, compensationReasonType) {
    queryClient.invalidateQueries(
      queryKeys.employeeCompensationRecords(employeeId),
    );
    this.onChangePaySchedule(queryClient);
    this.onChangeReason(queryClient, compensationReasonType);
  },
  onChangeBonusRecord(queryClient, employeeId, type) {
    queryClient.invalidateQueries(queryKeys.employeeBonusRecords(employeeId));
    this.onChangeReason(queryClient, type);
  },
  onSurveyComplete(queryClient, { requestId }) {
    queryClient.invalidateQueries([queryKeys.requests([])[0]]);
    queryClient.invalidateQueries(queryKeys.employeeRequest(requestId));
    queryClient.invalidateQueries(queryKeys.manageRequest(requestId));
    queryClient.invalidateQueries([queryKeys.employeeAssessmentSurveys()[0]]);
  },
  onChangeAssetCategory(queryClient) {
    queryClient.invalidateQueries(queryKeys.assetCategories());
    this.onLookupChange(queryClient, lookupsModels.assetCategory);
    queryClient.invalidateQueries([queryKeys.employeeAssets()[0]]);
  },
  onChangeEmployeeAsset(queryClient, employeeId) {
    queryClient.invalidateQueries(queryKeys.employeeAssets(employeeId));
    this.onChangeAssetCategory(queryClient);
  },
  onChangeEmployeeJobInfo(queryClient, { reportTo }) {
    if (reportTo) {
      if (_.isArray(reportTo)) {
        reportTo.forEach((id) => {
          queryClient.invalidateQueries(queryKeys.employeeSubordinates(id));
        });
      } else {
        queryClient.invalidateQueries(queryKeys.employeeSubordinates(reportTo));
      }
    }
  },
  onChangeCompanyIntegrations(queryClient, companyId = 1) {
    queryClient.invalidateQueries(
      queryKeys.companyActiveIntegrations(companyId),
    );
  },
  onChangeFiles(queryClient, { contentId, contentType }) {
    queryClient.invalidateQueries({
      predicate: ({ queryKey }) =>
        queryKey?.length > 2 &&
        queryKey[1] === contentId &&
        queryKey[2] === contentType,
    });
  },
  onSendSignatureRequest(queryClient) {
    queryClient.invalidateQueries([queryKeys.requests([])[0]]);
    queryClient.invalidateQueries([queryKeys.request()[0]]);
    queryClient.invalidateQueries([queryKeys.files()[0]]);
  },
  onSignRequestedDocument(queryClient, { contentId, contentType, requestId }) {
    this.onSendSignatureRequest(queryClient, { contentId, contentType });
    queryClient.invalidateQueries(queryKeys.employeeRequest(requestId));
    queryClient.invalidateQueries(queryKeys.manageRequest(requestId));
  },
  onChangeEmployeesRole(queryClient) {
    queryClient.invalidateQueries(queryKeys.allRoles()[0]);
  },
  onChangeCompensationGroup(queryClient) {
    queryClient.invalidateQueries(queryKeys.compensationGroups());
  },
  onChangeCompensationBand(queryClient) {
    queryClient.invalidateQueries(queryKeys.compensationGroups());
    queryClient.invalidateQueries(queryKeys.compensationBands());
  },
  onChangeHolidays(queryClient) {
    queryClient.invalidateQueries([queryKeys.holidays()[0]]);
    queryClient.invalidateQueries([queryKeys.employeeHolidays()[0]]);
  },
  onChangeWidget(queryClient) {
    queryClient.invalidateQueries(queryKeys.widgets());
  },
  onChangeCandidateOffer(queryClient, { candidateId }) {
    queryClient.invalidateQueries(queryKeys.candidate(candidateId));
    queryClient.invalidateQueries([queryKeys.candidatesOffers()[0]]);
  },
  onChangeLinkCategory(queryClient) {
    queryClient.invalidateQueries(queryKeys.companyLinksCategories());
    this.onLookupChange(queryClient, lookupsModels.link);
    this.onChangeCompanyLink(queryClient);
  },
  onChangeCompanyLink(queryClient) {
    queryClient.invalidateQueries(queryKeys.companyLinks());
    this.onLookupChange(queryClient, lookupsModels.link);
  },
  onChangeAnnounce(queryClient) {
    queryClient.invalidateQueries(queryKeys.announcements()[0]);
    queryClient.resetQueries([queryKeys.announcement()[0]]);
  },
  onChangeRequestStatus(queryClient) {
    queryClient.invalidateQueries([queryKeys.requests([])[0]]);
    queryClient.invalidateQueries([queryKeys.requestsCount()[0]]);
  },
  onChangeSatisfaction(queryClient) {
    queryClient.invalidateQueries(queryKeys.satisfactionSurvey());
    this.onChangeSatisfactionByHash(queryClient);
  },
  onChangeSatisfactionByHash(queryClient) {
    queryClient.invalidateQueries([queryKeys.satisfactionSurveyByHash()[0]]);
  },
  onChangeTimeTrackingGroup(queryClient) {
    queryClient.invalidateQueries(queryKeys.timeTrackingGroups());
    queryClient.invalidateQueries([queryKeys.employee()[0]]);
  },
  onChangeHirePacket(queryClient) {
    queryClient.invalidateQueries(queryKeys.hirePackets());
  },
  onChangeWorkPattern(queryClient) {
    queryClient.invalidateQueries(queryKeys.workPatterns());
    queryClient.invalidateQueries([queryKeys.employeeWorkPatterns()[0]]);
  },
  onChangeEmployeeWorkPattern(employeeId, queryClient) {
    queryClient.invalidateQueries(queryKeys.employeeWorkPatterns(employeeId));
    queryClient.invalidateQueries(queryKeys.workPatterns());
  },
  onFinalizePayroll(queryClient) {
    queryClient.invalidateQueries([queryKeys.loadPayrolls()[0]]);
  },
  onChangeExtraPayRecord(queryClient, employeeId, type) {
    queryClient.invalidateQueries(
      queryKeys.employeeExtraPayRecords(employeeId),
    );
    queryClient.invalidateQueries(
      queryKeys.reasons({ filter: { byType: type } }),
    );
    queryClient.invalidateQueries([queryKeys.loadPayrollReport()[0]]);
  },
  onChangeTaxBand(queryClient) {
    queryClient.invalidateQueries([queryKeys.taxBands()[0]]);
  },
  onChangeAllowance(queryClient) {
    queryClient.invalidateQueries([queryKeys.allowances()[0]]);
  },
  onChangeDeduction(queryClient) {
    queryClient.invalidateQueries([queryKeys.deductions()[0]]);
  },
  onDetachAllowance(queryClient) {
    queryClient.invalidateQueries([queryKeys.employeeAllowances()[0]]);
  },
  onDetachDeduction(queryClient) {
    queryClient.invalidateQueries([queryKeys.employeeDeductions()[0]]);
  },
  onChangeNotifications(queryClient) {
    queryClient.invalidateQueries([queryKeys.notifications()[0]]);
    queryClient.invalidateQueries([queryKeys.requestsCount()[0]]);
  },
  onChangePerformanceCycles(queryClient) {
    queryClient.invalidateQueries([queryKeys.performanceCycles()[0]]);
  },
  onChangeSurveys(queryClient) {
    queryClient.invalidateQueries([queryKeys.surveys(['all'])[0]]);
  },
  onChangeAllFiles(queryClient) {
    queryClient.invalidateQueries([queryKeys.files()[0]]);
  },
  onUpdatePermissionsVisibility(queryClient) {
    queryClient.invalidateQueries([queryKeys.groupedPermissions()]);
  },
};
